import { Helmet } from 'react-helmet-async';
import { generateOrganizationStructuredData } from '@/utils/seoHelpers';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: object;
  pageType?: 'homepage' | 'horse' | 'jockey' | 'race' | 'article' | 'racetrack';
  breadcrumbs?: Array<{label: string; href?: string}>;
}

export const SEOHead = ({
  title = "Horsebeat.cz – Spolumajitelství dostihových koní (syndikát) a statistiky",
  description = "Spolumajitelství dostihových koní (formálně syndikát) pro fanoušky i začátečníky. Podrobné statistiky koní a žokejů, kompletní databáze českých dostihů. Zažijte dostihy naplno s Horsebeat.cz!",
  keywords = "spolumajitelství dostihového koně, dostihový syndikát, podíl v koni, dostihy, koně, žokejové, statistiky, Velká Pardubická, hipodrom, galop, fantasy dostihy, výsledky dostihů, jockey statistiky, Pardubice, Velká Chuchle, dostihová komunita, dostihový sport, česká závodiště, překážkové dostihy, rovinové dostihy",
  canonical,
  ogType = "website",
  ogImage = "https://horsebeat.cz/og-image.jpg",
  structuredData,
  pageType = 'homepage',
  breadcrumbs
}: SEOProps) => {
  const fullTitle = title.includes("Horsebeat.cz") ? title : `${title} | Horsebeat.cz`;
  const currentUrl = canonical || (typeof window !== 'undefined' ? window.location.href : 'https://horsebeat.cz');
  const absoluteOgImage = ogImage?.startsWith('http') ? ogImage : `https://horsebeat.cz${ogImage?.startsWith('/') ? '' : '/'}${ogImage || 'og-image.jpg'}`;

  // Map Czech marketing pages to their English counterparts for hreflang
  const CZ_TO_EN_HREFLANG: Record<string, string> = {
    '/': '/en',
    '/syndicates': '/en/syndicates',
    '/o-nas': '/en/about',
  };
  const currentPath = typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') || '/' : '';
  const enAlt = CZ_TO_EN_HREFLANG[currentPath];

  // Generate additional structured data based on page type
  const additionalStructuredData: object[] = [];
  
  if (pageType === 'homepage') {
    additionalStructuredData.push(generateOrganizationStructuredData());
  }

  // Add breadcrumb structured data if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    additionalStructuredData.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.label,
        ...(item.href && { "item": `https://horsebeat.cz${item.href}` })
      }))
    });
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="cs" />
      <meta name="geo.region" content="CZ" />
      <meta name="geo.country" content="Czech Republic" />
      <meta name="author" content="Horsebeat.cz" />
      <meta name="revisit-after" content="1 day" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* hreflang alternates for translated marketing pages */}
      {enAlt && <link rel="alternate" hrefLang="cs" href={`https://horsebeat.cz${currentPath === '/' ? '' : currentPath}`} />}
      {enAlt && <link rel="alternate" hrefLang="en" href={`https://horsebeat.cz${enAlt}`} />}
      {enAlt && <link rel="alternate" hrefLang="x-default" href={`https://horsebeat.cz${currentPath === '/' ? '' : currentPath}`} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - Horsebeat.cz`} />
      <meta property="og:locale" content="cs_CZ" />
      <meta property="og:site_name" content="Horsebeat.cz" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      <meta name="twitter:image:alt" content={`${title} - Horsebeat.cz`} />
      
      {/* Article specific meta tags */}
      {pageType === 'article' && (
        <>
          <meta property="article:publisher" content="https://horsebeat.cz" />
          <meta property="article:section" content="Dostihový sport" />
          <meta property="article:tag" content="dostihy, koně, sport" />
        </>
      )}

      {/* Primary Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Additional Structured Data */}
      {additionalStructuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};